import {Component, Input, OnInit} from '@angular/core';
import {
    ClassificationDetailsResponse,
    ClassificationForecast,
    ClassificationType,
    IncidentSubtype,
    ParticipantRole,
    ReferenceBookResponse,
    ReferenceBookType,
    TransactionType,
    TypologyApiService,
} from '@npck/af-frontend-api';
import {DATE_SERVER_FORMAT, Spinner} from '@npck/libs/commons';
import {DialogService} from '@npck/libs/ui-kit';
import {combineLatest} from 'rxjs';
import {DateRangeFormValue} from '../../../../../components/date-range-picker-field/date-range-picker-field.component';
import {getFormattedDateOrNull} from '../../../../../components/full-table/filter/filter.component';
import {KZT_CURRENCY_CODE} from '../../../../../domain/constants';
import {DictionaryHolderService, ReferenceBookTypeExtended} from '../../../../../domain/dictionary-holder.service';
import {
    ClassificationTableCellDetailsDialogComponent,
    ClassificationTableCellDetailsDialogData,
} from './clasification-table-cell/classification-details-dialog/classification-table-cell-details-dialog.component';

const INCIDENT_SUBTYPE_COMMON_ROWS: ClassificationRow[] = [
    'incidentCount',
    'amountIncidentInCurrency',
    'amountTransactionsInCurrency',
    'currencyTransactions',
    'timesOfDay',
    'dayOfWeek',
    'transactionLastDate',
    'transactionType',
    'damageRate',
    'transactionCountPerVictimByIncident',
    'transactionTimeInterval',
    'transactionWithdrawalTimeInterval',
];

const TRANSACTION_TYPE_COMMON_ROWS: ClassificationRow[] = [
    'incidentCount',
    'transactionLastDate',
    'amountTransactionsInCurrency',
    'currencyTransactions',
    'fraudType',
    'timesOfDay',
    'dayOfWeek',
    'transactionStatus',
    'transactionCountPerVictimByIncident',
    'transactionTimeInterval',
    'transactionCountPerSuspect',
];

const SUBJECT_COMMON_ROWS: ClassificationRow[] = [
    'subjectType',
    'subjectGender',
    'subjectAge',
    'subjectRegion',
    'subjectCountry',
];

@Component({
    selector: 'af-classification-table',
    standalone: false,
    templateUrl: './classification-table.component.html',
    styleUrl: './classification-table.component.scss',
})
export class ClassificationTableComponent implements OnInit {
    @Input() sectionTitle: string;
    @Input() type: ClassificationType;
    @Input() subtype: string;
    @Input() participantRole: ParticipantRole;
    @Input() context: TableContext;
    Object = Object;
    columns: ClassificationColumn[] = ['quarter', 'month', 'forecastFuture', 'forecastPast'];
    selectedCurrency: Record<string, string> = {
        'amountIncidentInCurrency': KZT_CURRENCY_CODE,
        'amountTransactionsInCurrency': KZT_CURRENCY_CODE,
    };
    dataSource: Record<string, RowViewModel>;

    rowsByContext: Record<TableContext, ClassificationRow[]> = {
        fraud: INCIDENT_SUBTYPE_COMMON_ROWS,
        transactions: TRANSACTION_TYPE_COMMON_ROWS,
        subject_suspect: SUBJECT_COMMON_ROWS.filter(row => row !== 'subjectRegion'),
        subject_victim: SUBJECT_COMMON_ROWS.filter(row => row !== 'subjectCountry'),
    };

    selectedDateRange: Record<ClassificationColumn, DateRangeFormValue> = this.columns.reduce((acc, column) => {
        acc[column] = null;
        return acc;
    }, {} as Record<ClassificationColumn, DateRangeFormValue>);

    constructor(
        private dictionaryHolderService: DictionaryHolderService,
        private typologyApiService: TypologyApiService,
        private dialogService: DialogService,
    ) {
    }

    ngOnInit(): void {
        this.dataSource = this.createEmptyDataSource();
        this.columns.forEach(column => Spinner.show(column));
    }

    loadDataForColumn(column: ClassificationColumn, dateRange?: DateRangeFormValue): void {
        this.selectedDateRange[column] = dateRange;
        let classificationForecast: ClassificationForecast;
        if (column === 'forecastFuture') {
            classificationForecast = ClassificationForecast.FORECAST_FUTURE;
        } else if (column === 'forecastPast') {
            classificationForecast = ClassificationForecast.FORECAST_PAST;
        }
        Spinner.show(column);
        combineLatest([
            this.dictionaryHolderService.getCustomizedReference(ReferenceBookType.FRAUD_TYPE),
            this.dictionaryHolderService.getCustomizedReference(ReferenceBookType.CURRENCY),
            this.dictionaryHolderService.getCustomizedReference(ReferenceBookType.TRANSACTION_STATUS),
            this.dictionaryHolderService.getCustomizedReference(ReferenceBookType.TRANSACTION_TYPE),
            this.dictionaryHolderService.getReference('subject_type'),
            this.dictionaryHolderService.getCustomizedReference(ReferenceBookType.GENDER),
            this.dictionaryHolderService.getCustomizedReference(ReferenceBookType.REGION),
            this.dictionaryHolderService.getCustomizedReference(ReferenceBookType.COUNTRY),
            this.typologyApiService.getClassificationDetails({
                groupedBy: this.type,
                subtype: this.type === ClassificationType.FRAUD_TYPE ? this.subtype as IncidentSubtype : null,
                transactionType: this.type === ClassificationType.TRANSACTION_TYPE ? this.subtype as TransactionType : null,
                participant: this.participantRole,
                classificationForecast: classificationForecast,
                startDate: getFormattedDateOrNull(dateRange.dateFrom, DATE_SERVER_FORMAT),
                endDate: getFormattedDateOrNull(dateRange.dateTo, DATE_SERVER_FORMAT),
                startDateForMonth: classificationForecast === ClassificationForecast.FORECAST_PAST
                    ? getFormattedDateOrNull(this.selectedDateRange.month?.dateFrom, DATE_SERVER_FORMAT) : null,
                endDateForMonth: classificationForecast === ClassificationForecast.FORECAST_PAST
                    ? getFormattedDateOrNull(this.selectedDateRange.month?.dateTo, DATE_SERVER_FORMAT) : null,
            }),
        ])
            .subscribe(
                ([fraudTypes, currencies, transactionStatuses, transactionTypes, subjectTypes, genders, regions, countries, response]) => {
                    const dictionaries: Record<ReferenceBookTypeExtended, ReferenceBookResponse[]> = {
                        [ReferenceBookType.FRAUD_TYPE]: fraudTypes,
                        [ReferenceBookType.TRANSACTION_STATUS]: transactionStatuses,
                        [ReferenceBookType.TRANSACTION_TYPE]: transactionTypes,
                        [ReferenceBookType.CURRENCY]: currencies,
                        [ReferenceBookType.GENDER]: genders,
                        [ReferenceBookType.REGION]: regions,
                        [ReferenceBookType.COUNTRY]: countries,
                        subject_type: subjectTypes,
                    } as Record<ReferenceBookTypeExtended, ReferenceBookResponse[]>;
                    this.mapToViewModel(column, response, dictionaries);
                });

        if (column === 'month' && this.selectedDateRange.forecastPast?.dateFrom && this.selectedDateRange.forecastPast?.dateTo) {
            this.loadDataForColumn('forecastPast', this.selectedDateRange.forecastPast);
        }
    }

    openCellDetails(
        row: ClassificationRow,
        column: ClassificationColumn,
        cell: CellViewModelType[],
    ): void {
        this.dialogService.openCustomDialogFitContent(
            ClassificationTableCellDetailsDialogComponent,
            <ClassificationTableCellDetailsDialogData>{
                row: row,
                selectedDateRange: this.selectedDateRange[column],
                translationPostfix: this.dataSource[row].translationPostfix,
                participantRole: this.participantRole,
                cellData: cell as CellViewModel[],
            });
    }

    private mapToViewModel(
        column: ClassificationColumn,
        response: ClassificationDetailsResponse,
        dictionaries: Record<ReferenceBookTypeExtended, ReferenceBookResponse[]>,
    ): void {
        Object.keys(response)
            .map(key => key as keyof ClassificationDetailsResponse)
            .filter(key => this.rowsByContext[this.context].includes(key))
            .forEach((key: keyof ClassificationDetailsResponse) => {
                switch (key) {
                    case 'incidentCount':
                    case 'timesOfDay':
                    case 'dayOfWeek':
                    case 'transactionLastDate':
                    case 'damageRate':
                    case 'subjectAge':
                    case 'subjectGender':
                    case 'transactionCountPerVictimByIncident':
                    case 'transactionCountPerSuspect':
                    case 'transactionTimeInterval':
                    case 'transactionWithdrawalTimeInterval':
                        this.dataSource[key].columns[column] = response[key].map(res => {
                            return <CellViewModel>{
                                label: res.name,
                                name: res.name,
                                value: res.value,
                                count: res.count,
                                hasAlert: res.hasAlert,
                            };
                        });
                        break;

                    case 'amountIncidentInCurrency':
                    case 'amountTransactionsInCurrency':
                        this.dataSource[key].columns[column] = response[key].map(res => <CurrencyCellViewModel>{
                            currency: dictionaries[ReferenceBookType.CURRENCY].find(item => item.code === res.currency),
                            cells: res.numericSummaries.map(value => <CellViewModel>{
                                label: value.name,
                                name: value.name,
                                value: value.value,
                                count: value.count,
                                hasAlert: value.hasAlert,
                            }),
                        });
                        break;
                    case 'transactionType':
                        this.dataSource[key].columns[column] = response[key].map(res => <CellViewModel>{
                            label: dictionaries[ReferenceBookType.TRANSACTION_TYPE].find(item => item.code === res.name).name_ru,
                            name: res.name,
                            value: res.value,
                            count: res.count,
                            hasAlert: res.hasAlert,
                        });
                        break;
                    case 'transactionStatus':
                        this.dataSource[key].columns[column] = response[key].map(res => {
                            return <CellViewModel>{
                                label: dictionaries[ReferenceBookType.TRANSACTION_STATUS].find(item => item.code === res.name).name_ru,
                                name: res.name,
                                value: res.value,
                                count: res.count,
                                hasAlert: res.hasAlert,
                            };
                        });
                        break;
                    case 'currencyTransactions':
                        this.dataSource[key].columns[column] = response[key].map(res => {
                            return <CellViewModel>{
                                label: dictionaries[ReferenceBookType.CURRENCY].find(item => item.code === res.name).code1,
                                name: res.name,
                                value: res.value,
                                count: res.count,
                                hasAlert: res.hasAlert,
                            };
                        });
                        break;
                    case 'fraudType':
                        this.dataSource[key].columns[column] = response[key].map(res => {
                            return <CellViewModel>{
                                label: dictionaries[ReferenceBookType.FRAUD_TYPE].find(item => item.code === res.name).name_ru,
                                name: res.name,
                                value: res.value,
                                count: res.count,
                                hasAlert: res.hasAlert,
                            };
                        });
                        break;
                    case 'subjectType':
                        this.dataSource[key].columns[column] = response[key].map(res => {
                            return <CellViewModel>{
                                label: dictionaries.subject_type.find(item => item.code === res.name).name_ru,
                                name: res.name,
                                value: res.value,
                                count: res.count,
                                hasAlert: res.hasAlert,
                            };
                        });
                        break;
                    case 'subjectRegion':
                        this.dataSource[key].columns[column] = response[key].map(res => {
                            return <CellViewModel>{
                                label: dictionaries[ReferenceBookType.REGION]
                                    .find(item => item.code === res.name)?.name_ru || 'i18n_af_emptyStateTitleNoData',
                                name: res.name,
                                value: res.value,
                                count: res.count,
                                hasAlert: res.hasAlert,
                            };
                        });
                        break;
                    case 'subjectCountry':
                        this.dataSource[key].columns[column] = response[key].map(res => {
                            return <CellViewModel>{
                                label: dictionaries[ReferenceBookType.COUNTRY]
                                    .find(item => item.code === res.name)?.name_ru || 'i18n_af_emptyStateTitleNoData',
                                name: res.name,
                                value: res.value,
                                count: res.count,
                                hasAlert: res.hasAlert,
                            };
                        });
                        break;
                }
            });
        Spinner.hide(column);
    }

    private createEmptyDataSource(): Record<string, RowViewModel> {
        return this.rowsByContext[this.context].reduce((acc, row) => {
            acc[row as ClassificationRow] = this.createEmptyRow(row);
            return acc;
        }, {} as Record<ClassificationRow, RowViewModel>);
    }

    private createEmptyRow(row: ClassificationRow): RowViewModel {
        const values = this.columns
            .reduce((acc2, column) => {
                acc2[column as ClassificationColumn] = [];
                return acc2;
            }, {} as Record<ClassificationColumn, CellViewModel[]>);
        switch (row) {
            case 'incidentCount':
                return <RowViewModel>{
                    dataType: 'count',
                    maxDataRows: 1,
                    rowType: 'count',
                    columns: values,
                };
            case 'amountIncidentInCurrency':
            case 'amountTransactionsInCurrency':
                return <RowViewModel>{
                    dataType: 'money',
                    translationPostfix: 'Summary',
                    columns: values,
                    hasCurrencyFilter: true,
                };
            case 'currencyTransactions':
            case 'transactionType':
            case 'fraudType':
            case 'transactionStatus':
            case 'subjectType':
            case 'subjectRegion':
            case 'subjectCountry':
                let maxDataRows: number = 2;

                if (['subjectCountry',
                    'subjectRegion',
                    'currencyTransactions',
                    'transactionStatus'].includes(row)) {
                    maxDataRows = 3;
                }
                return <RowViewModel>{
                    maxDataRows: maxDataRows,
                    dataType: 'percentage',
                    columns: values,
                };
            case 'timesOfDay':
                return <RowViewModel>{
                    maxDataRows: 2,
                    translationPostfix: 'DayTime',
                    dataType: 'percentage',
                    columns: values,
                };
            case 'dayOfWeek':
                return <RowViewModel>{
                    maxDataRows: 3,
                    translationPostfix: 'DayOfWeek',
                    dataType: 'percentage',
                    columns: values,
                };
            case 'subjectAge':
                return <RowViewModel>{
                    maxDataRows: 3,
                    translationPostfix: 'SubjectAge',
                    dataType: 'percentage',
                    columns: values,
                };
            case 'subjectGender':
                return <RowViewModel>{
                    maxDataRows: 2,
                    translationPostfix: 'SubjectGender',
                    dataType: 'percentage',
                    columns: values,
                };
            case 'damageRate':
                return <RowViewModel>{
                    dataType: 'percentage',
                    translationPostfix: 'Damage',
                    columns: values,
                };
            case 'transactionLastDate':
                return <RowViewModel>{
                    maxDataRows: 1,
                    columns: values,
                };
            case 'transactionCountPerVictimByIncident':
            case 'transactionCountPerSuspect':
                return <RowViewModel>{
                    translationPostfix: 'Summary',
                    columns: values,
                };
            case 'transactionTimeInterval':
            case 'transactionWithdrawalTimeInterval':
                return <RowViewModel>{
                    translationPostfix: 'Summary',
                    columns: values,
                };
        }
    }
}

export interface RowViewModel {
    maxDataRows?: number;
    translationPostfix?: string;
    columns: Record<ClassificationColumn, CellViewModelType[]>
    dataType?: CellDataType;
    hasCurrencyFilter?: boolean;
}

export interface CellViewModel {
    value: number | string;
    count: number;
    label?: string;
    hasAlert: boolean;
}

export interface CurrencyCellViewModel {
    currency: ReferenceBookResponse;
    cells: CellViewModel[];
}

export declare type CellViewModelType = CellViewModel | CurrencyCellViewModel;

export declare type TableContext = 'transactions' | 'fraud' | 'subject_victim' | 'subject_suspect'

export declare type CellDataType = 'percentage' | 'count' | 'money';

export declare type ClassificationRow = keyof ClassificationDetailsResponse;

export declare type ClassificationColumn = 'quarter' | 'month' | 'forecastFuture' | 'forecastPast';
